import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { showToast } from '@ideoz/ui';
import { queryClient } from '../../lib/queryClient';
import {
  checkVisitor,
  logout as logoutAction,
  logoutUser,
  removeAccessToken,
  selectUser,
  useAppDispatch,
  useAppSelector,
  useMyProfilePageFacade,
} from '@ideoz/core';
import { useAuthDialog } from '../../hooks/useAuthDialog';
import { useWebNavigation } from '../../hooks/useWebNavigation';
import { useAccountSidebarNavItems } from '../../hooks/useAccountSidebarNavItems';
import type { AccountNavSection } from '@ideoz/ui';

/**
 * Logic for the user-account profile page: the shared profile facade plus the
 * web-specific logout, auth-dialog adapter, and the startup sidebar (so
 * /profile mirrors the startup shell's nav without highlighting a section).
 */
export function useMyProfile(activeSection: AccountNavSection = 'profile') {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const authDialog = useAuthDialog();
  const navigation = useWebNavigation();

  const auth = useMemo(
    () => ({
      isOpen: authDialog.isOpen,
      mode: authDialog.mode,
      isLoading: authDialog.isLoading,
      openLogin: authDialog.openLogin,
      openRegister: authDialog.openRegister,
      close: authDialog.closeDialog,
    }),
    [authDialog]
  );

  // JWT sent via Authorization header; removeAccessToken clears both tokens.
  const handleLogout = useCallback(async () => {
    if (!user) return;
    try {
      await logoutUser();
      removeAccessToken();
      dispatch(logoutAction());
      // Clear the react-query cache too — it's a separate in-memory store the
      // Redux logout reset doesn't touch, so without this the next account
      // sees the previous user's cached startups/attachments until refresh.
      queryClient.clear();
      await checkVisitor(dispatch);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      showToast({ message: t('profile.logoutFailed'), severity: 'error' });
    }
  }, [user, dispatch, navigate, t]);

  const logout = useMemo(() => ({ logout: handleLogout }), [handleLogout]);

  const facade = useMyProfilePageFacade({ navigation, auth, logout, showToast });

  // Account-level page (shared by /profile and /app-settings): show the account
  // nav (My Startups / Profile / Billing / App Settings), not the startup-scoped
  // nav — these pages aren't under a startup, so scope items are meaningless.
  const accountNavItems = useAccountSidebarNavItems(activeSection);

  const facadeWithStartupSidebar = useMemo(
    () => ({
      ...facade,
      sidebar: { ...facade.sidebar, navigationItems: accountNavItems },
    }),
    [facade, accountNavItems]
  );

  return { user, authDialog, facade: facadeWithStartupSidebar };
}
